import React, { useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Fade,
  IconButton,
  Modal,
  Typography,
  Stack,
  Grid,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import ChargeReasonCodeSelect from "./ChargeReasonCodeSelect";
import AllowanceReasonCodeSelect from "./AllowanceReasonCodeSelect";
import CustomTextInput from "../CustomTextInput";
import { currencyFormatter } from "../../utils/currencyFormat";
import {
  ABSOLUTE,
  ALLOWANCE,
  DEFAULT_TAX_SCHEME,
  PERCENTAGE,
} from "../../constants/defaults";
import { createAllowanceChargeSchema } from "../../validations/allowanceChargeValidation";
import TaxCategoryFields from "../TaxCategoryField";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  borderRadius: "15px",
  boxShadow: 24,
};

const formatAmount = (value) =>
  currencyFormatter().format(!isNaN(value) ? value : 0);

const getTypeButtonStyles = (isActive) => ({
  flex: 1,
  py: 1.5,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  backgroundColor: isActive ? "white" : "grey.100",
  borderColor: isActive ? "primary.main" : "grey.300",
  color: isActive ? "primary.main" : "grey.500",
  "&:hover": {
    backgroundColor: isActive ? "grey.50" : "grey.200",
    borderColor: isActive ? "primary.main" : "grey.400",
  },
  "& .MuiSvgIcon-root": {
    color: isActive ? "primary.main" : "grey.500",
  },
});

const calculateAmount = (baseAmount, percentage) => {
  return ((Number(baseAmount) || 0) * (Number(percentage) || 0)) / 100;
};

const calculateNewNetAmount = (
  currentAmount,
  calculatedAmount,
  allowanceType,
) => {
  const current = Number(currentAmount) || 0;
  const calculated = Number(calculatedAmount) || 0;
  return allowanceType === ALLOWANCE
    ? current - calculated
    : current + calculated;
};

const AmountRow = ({ label, value, isLast = false, isNegative = false }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{
      display: "flex",
      justifyContent: "space-between",
      mb: isLast ? (isNegative ? 1.5 : 0) : 1,
      fontWeight: 600,
    }}
  >
    <span>{label}:</span>
    <span>{value}</span>
  </Typography>
);

// ---------- Main Component ----------
const AddAllowanceChargeModal = ({
  open,
  onClose,
  onAdd,
  type,
  initialFormData,
  editIndex,
  currentNetAmount,
  itemBaseAmount,
  isDocumentLevel = false,
}) => {
  const isAllowance = type === ALLOWANCE;
  const isEditMode = !!initialFormData && editIndex !== undefined;

  // --- Helpers ---
  const determineType = (data) => {
    if (!data) return PERCENTAGE; // Default to PERCENTAGE on initial stste
    const multiplier = data?.multiplier_factor_numeric;
    const base = data?.base_amount;

    return multiplier && base ? PERCENTAGE : ABSOLUTE;
  };

  const getFormValues = (data) => {
    const formType = determineType(data);
    const multiplier = data?.multiplier_factor_numeric;
    const base = data?.base_amount;
    const amount = data?.amount;

    return {
      type: formType,
      base_amount: base ?? itemBaseAmount,
      value: formType === PERCENTAGE ? (multiplier ?? 0) : (amount ?? 0),
      reason_code: data?.reason_code ?? "",
      reason: data?.reason ?? "",
      ...(isDocumentLevel && {
        tax_category: {
          id: data?.tax_category?.id ?? null,
          percent: data?.tax_category?.percent ?? 0,
          tax_exemption_reason_code:
            data?.tax_category?.tax_exemption_reason_code ?? null,
          tax_exemption_reason:
            data?.tax_category?.tax_exemption_reason ?? null,
          zero_rated_reason_code:
            data?.tax_category?.zero_rated_reason_code ?? null,
          zero_rated_reason: data?.tax_category?.zero_rated_reason ?? null,
        },
      }),
    };
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(
      createAllowanceChargeSchema(
        currentNetAmount,
        itemBaseAmount,
        type,
        true,
        true,
        isDocumentLevel,
      ),
    ),
    defaultValues: getFormValues(initialFormData),
  });
  useEffect(() => {
    if (open) reset(getFormValues(initialFormData));
  }, [open, initialFormData, itemBaseAmount, reset, type]);

  // Watch fields
  const watchedType = useWatch({ control, name: "type" });
  const watchedBaseAmount = useWatch({ control, name: "base_amount" });
  const watchedValue = useWatch({ control, name: "value" });

  const calculatedAmount =
    watchedType === PERCENTAGE
      ? calculateAmount(watchedBaseAmount, watchedValue)
      : Number(watchedValue) || 0;

  // --- Handlers ---
  const handleAdd = (data) => {
    const calculatedAmount =
      data.type === PERCENTAGE
        ? calculateAmount(data.base_amount, data.value)
        : Number(data.value) || 0;

    const formattedData = {
      reason_code: data.reason_code,
      reason: data.reason,
      amount: calculatedAmount,
      multiplier_factor_numeric: data.type === PERCENTAGE ? data.value : null,
      base_amount: data.type === PERCENTAGE ? data.base_amount : null,
      ...(isDocumentLevel && {
        tax_category: {
          id: data?.tax_category?.id ?? "",
          percent: data?.tax_category?.percent ?? 0,
          tax_exemption_reason_code:
            data?.tax_category?.tax_exemption_reason_code ?? null,
          tax_exemption_reason:
            data?.tax_category?.tax_exemption_reason ?? null,
          tax_scheme: data?.tax_category?.tax_scheme ?? DEFAULT_TAX_SCHEME,
        },
      }),
    };

    onAdd(formattedData);
    onClose();
  };

  const handleReset = () => reset(getFormValues(initialFormData));

  const handleTypeChange = (newType) => {
    setValue("type", newType);
    setValue("base_amount", newType === ABSOLUTE ? null : itemBaseAmount);
    setValue("value", 0);
    clearErrors(["value", "base_amount"]);
  };

  const labels = {
    title: isEditMode
      ? isAllowance
        ? "Edit Allowance"
        : "Edit Charge"
      : isAllowance
        ? "Add Allowance"
        : "Add Charge",
    value: isAllowance ? "Allowance Value" : "Charge Value",
  };

  const newNetAmount = calculateNewNetAmount(
    currentNetAmount,
    calculatedAmount,
    type,
  );
  const isNegative = newNetAmount < 0;

  return (
    <Modal
      open={open}
      onClose={(_, reason) => reason !== "backdropClick" && onClose()}
      className="backdrop-model"
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
          >
            <Typography variant="h6">{labels.title}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          {/* Fields */}
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Calculation Method */}
              <Stack spacing={1}>
                <Typography variant="subtitle2">Calculation Method</Typography>
                <Stack direction="row" spacing={1}>
                  {[ABSOLUTE, PERCENTAGE].map((btnType) => {
                    const Icon =
                      btnType === ABSOLUTE ? AttachMoneyIcon : PercentIcon;
                    return (
                      <Button
                        key={btnType}
                        variant="outlined"
                        onClick={() => handleTypeChange(btnType)}
                        sx={getTypeButtonStyles(watchedType === btnType)}
                      >
                        <Icon fontSize="small" />
                        <Typography variant="caption" fontWeight="bold">
                          {btnType}
                        </Typography>
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>

              {/* Value Input */}
              {watchedType === PERCENTAGE ? (
                <Stack spacing={2}>
                  <CustomTextInput
                    data-testid="base_amount"
                    name="base_amount"
                    control={control}
                    label="Base Amount"
                    type="number"
                    errors={errors}
                  />
                  <CustomTextInput
                    data-testid="value"
                    name="value"
                    control={control}
                    label="Percentage (%)"
                    type="number"
                    errors={errors}
                  />
                </Stack>
              ) : (
                <CustomTextInput
                  data-testid="value"
                  name="value"
                  control={control}
                  label={labels.value}
                  type="number"
                  errors={errors}
                />
              )}

              {/* Calculated Amount & Net Impact */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: "grey.100",
                  border: `1px solid ${isNegative ? "error.main" : "grey.300"}`,
                }}
              >
                <AmountRow
                  label="Current total before tax"
                  value={formatAmount(currentNetAmount)}
                  isNegative={isNegative}
                />
                <AmountRow
                  label={
                    isDocumentLevel
                      ? `${isAllowance ? "Document Allowance" : "Document Charge"} amount`
                      : `${isAllowance ? "Allowance" : "Charge"} amount`
                  }
                  value={formatAmount(calculatedAmount)}
                  isNegative={isNegative}
                />
                <AmountRow
                  label="New total before tax"
                  value={formatAmount(newNetAmount)}
                  isNegative={isNegative}
                  isLast
                />
              </Box>

              {/* Reason */}
              <Stack spacing={2}>
                {isAllowance ? (
                  <AllowanceReasonCodeSelect
                    data-testid="reason_code"
                    name="reason_code"
                    control={control}
                    error={!!errors.reason_code}
                    helperText={errors.reason_code?.message || ""}
                    label="Reason Code"
                  />
                ) : (
                  <ChargeReasonCodeSelect
                    data-testid="reason_code"
                    name="reason_code"
                    control={control}
                    error={!!errors.reason_code}
                    helperText={errors.reason_code?.message || ""}
                    label="Reason Code"
                  />
                )}
                <CustomTextInput
                  data-testid="reason"
                  name="reason"
                  control={control}
                  label="Custom Reason"
                  errors={errors}
                />
              </Stack>

              {/* Tax Category & Rate - Only for Document Level */}
              {isDocumentLevel && (
                <Grid container spacing={2} mt={1}>
                  <TaxCategoryFields
                    data-testid="tax-category-fields"
                    getValues={getValues}
                    control={control}
                    errors={errors}
                    taxCategoryName="tax_category.id"
                    taxRateName="tax_category.percent"
                    setValue={setValue}
                    taxCategoryReadOnly={false}
                  />
                </Grid>
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Footer */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{
              px: 2,
              py: 2,
              borderTop: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Button variant="outlined" onClick={handleReset}>
              RESET
            </Button>
            <Button variant="contained" onClick={handleSubmit(handleAdd)}>
              {isEditMode ? "UPDATE" : "ADD"}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AddAllowanceChargeModal;
